import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ArrowUpRight } from 'lucide-react';

const Services = () => {
  const [openFaq, setOpenFaq] = useState(0);

  const benefits = [
    {
      title: "Experienced team",
      description: "Emphasize the expertise and knowledge of your team in developing and implementing neural networks",
      color: "from-cyan-400 to-cyan-500"
    },
    {
      title: "Tailored solutions",
      description: "Mention your ability to customize solutions based on specific business requirements",
      color: "from-blue-400 to-blue-500"
    },
    {
      title: "Cutting-edge technology",
      description: "Highlight your use of the latest tools and techniques in neural network development",
      color: "from-blue-500 to-blue-600"
    },
    {
      title: "Proven track record",
      description: "Showcase successful case studies or client testimonials that demonstrate the effectiveness of your services",
      color: "from-purple-400 to-purple-500"
    }
  ];

  const services = [
    {
      title: "Intelligent Automation",
      description: "Streamline operations with AI-powered automation solutions that reduce manual work and increase efficiency.",
      gradient: "from-cyan-400 to-blue-500",
      pattern: "radial"
    },
    {
      title: "OpenAI Integration",
      description: "Leverage cutting-edge OpenAI technologies to build intelligent applications and enhance user experiences.",
      gradient: "from-blue-500 to-blue-600",
      logo: true
    },
    {
      title: "Real-Time Data Processing",
      description: "Provide expert guidance on developing an AI strategy and integrating AI into your business processes",
      gradient: "from-blue-300 to-gray-300",
      pattern: "waves"
    },
    {
      title: "Recommendation Systems",
      description: "Develop and deploy custom machine learning models to extract insights and make predictions from your data.",
      gradient: "from-slate-700 to-slate-800",
      pattern: "lines"
    }
  ];

  const techCategories = [
    "Computer Vision",
    "AI in Healthcare",
    "Cybersecurity",
    "Machine Learning",
    "Data Analytics"
  ];

  const faqs = [
    {
      question: "01. What is Artificial Intellegance?",
      answer: "Machine Learning is a subset of AI that focuses on developing algorithms and models that allow computers to learn from data and improve their performance over time."
    },
    {
      question: "02. How does Machine Learning relate to Artificial Intelligence?",
      answer: "Machine Learning is a subset of AI that focuses on developing algorithms and models that enable computers to learn from data without being explicitly programmed."
    },
    {
      question: "03. Is Artificial Intelligence replacing human jobs?",
      answer: "AI is transforming jobs rather than simply replacing them. While some routine tasks may be automated, AI creates new opportunities and roles that require human creativity, emotional intelligence, and strategic thinking."
    },
    {
      question: "04. What are the different types of Artificial Intelligence?",
      answer: "There are three main types: Narrow AI (designed for specific tasks), General AI (human-level intelligence), and Super AI (surpassing human intelligence). Currently, most AI systems are Narrow AI."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Hero Banner */}
      <section className="relative h-[60vh] overflow-hidden mt-16">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=1600&h=900&fit=crop" 
            alt="Services"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/80 to-cyan-400/60"></div>
        </div>

        {/* Vertical "Neural networks" text on right */}
        <div className="absolute right-8 top-1/2 transform -translate-y-1/2 -rotate-90 origin-center">
          <h2 className="text-white text-6xl md:text-8xl font-bold opacity-50 whitespace-nowrap">
            Neural networks
          </h2>
        </div>

        <div className="relative container mx-auto px-6 h-full flex flex-col justify-end pb-12">
          <h1 className="text-white text-6xl md:text-7xl font-bold mb-8">_Services</h1>
          
          {/* Breadcrumb Bar */}
          <div className="bg-gradient-to-r from-blue-500/90 to-cyan-400/90 backdrop-blur-sm rounded-full px-8 py-4 flex items-center justify-between max-w-5xl">
            <div className="flex items-center gap-4 text-white">
              <a href="/" className="hover:underline">Home</a>
              <span>_</span>
              <span className="font-semibold">Services</span>
            </div>
            <div className="text-white font-mono text-sm hidden md:block">
              Innovative projects driven by the power of neural networks
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-6 bg-gray-100">
        <div className="container mx-auto">
          <div className="mb-16">
            <div className="text-sm text-gray-500 mb-4">(_services)</div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
              Distinct benefits and competitive advantages of our features
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                <div className="flex gap-2">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className={`w-3 h-3 rounded-sm bg-gradient-to-br ${benefit.color}`}></div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Cards Grid */}
      <section className="py-20 px-6 bg-gray-100">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Intelligent Automation - Top Left */}
            <div className="relative bg-gradient-to-br from-cyan-400 to-blue-500 rounded-3xl p-8 h-80 overflow-hidden group cursor-pointer">
              <div className="absolute top-8 left-8">
                <div className="flex gap-2 mb-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="w-4 h-4 bg-white/30 rounded"></div>
                  ))}
                </div>
              </div>
              
              {/* Abstract 3D Shape */}
              <div className="absolute right-8 top-12 w-48 h-48 opacity-40">
                <div className="w-full h-full bg-gradient-to-br from-blue-400/50 to-transparent rounded-full blur-2xl"></div>
              </div>

              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-white text-3xl font-bold mb-4">
                  Intelligent<br />Automation
                </h3>
              </div>

              <button className="absolute bottom-8 right-8 w-12 h-12 bg-black rounded-full flex items-center justify-center text-white hover:scale-110 transition">
                <ArrowUpRight className="w-6 h-6" />
              </button>
            </div>

            {/* OpenAI Powered - Top Right (Tall) */}
            <div className="relative bg-gradient-to-br from-blue-500 to-blue-600 rounded-3xl p-8 h-80 md:row-span-2 md:h-[41rem] overflow-hidden group cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-transparent"></div>
              
              <div className="relative h-full flex flex-col">
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center text-white">
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <svg className="w-12 h-12" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z"/>
                      </svg>
                      <span className="text-4xl font-bold">OpenAI</span>
                    </div>
                    <p className="text-sm text-white/80">Powered by</p>
                  </div>
                </div>
              </div>

              <button className="absolute bottom-8 right-8 w-12 h-12 bg-black rounded-full flex items-center justify-center text-white hover:scale-110 transition">
                <ArrowUpRight className="w-6 h-6" />
              </button>
            </div>

            {/* Real-Time Data Processing - Bottom Left */}
            <div className="relative bg-gradient-to-br from-blue-300 via-blue-200 to-gray-200 rounded-3xl p-8 h-80 overflow-hidden group cursor-pointer">
              {/* Wave pattern overlay */}
              <div className="absolute inset-0 opacity-40">
                <svg className="w-full h-full" preserveAspectRatio="none" viewBox="0 0 400 300">
                  <path d="M0,150 Q100,50 200,150 T400,150" fill="none" stroke="rgba(59, 130, 246, 0.3)" strokeWidth="40"/>
                  <path d="M0,180 Q100,80 200,180 T400,180" fill="none" stroke="rgba(59, 130, 246, 0.2)" strokeWidth="40"/>
                </svg>
              </div>

              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-gray-900 text-3xl font-bold mb-2">
                  Real-Time Data<br />Processing
                </h3>
                <p className="text-gray-700 text-sm">
                  Provide expert guidance on developing an AI strategy and integrating AI into your business processes
                </p>
              </div>

              <button className="absolute bottom-8 right-8 w-12 h-12 bg-black rounded-full flex items-center justify-center text-white hover:scale-110 transition">
                <ArrowUpRight className="w-6 h-6" />
              </button>
            </div>

            {/* Recommendation Systems - Bottom Right */}
            <div className="relative bg-gradient-to-br from-slate-700 to-slate-900 rounded-3xl p-8 h-80 overflow-hidden group cursor-pointer">
              {/* Line pattern overlay */}
              <div className="absolute inset-0">
                {[...Array(12)].map((_, i) => (
                  <div 
                    key={i}
                    className="absolute right-0 w-full border-t border-cyan-400/20"
                    style={{ 
                      top: `${20 + i * 7}%`,
                      borderRadius: '50%',
                      transform: `translateX(${i * 2}px)`
                    }}
                  ></div>
                ))}
              </div>

              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-white text-3xl font-bold mb-2">
                  Recommendation<br />Systems
                </h3>
                <p className="text-gray-300 text-sm">
                  Develop and deploy custom machine learning models to extract insights and make predictions from your data.
                </p>
              </div>

              <button className="absolute bottom-8 right-8 w-12 h-12 bg-white rounded-full flex items-center justify-center text-gray-900 hover:scale-110 transition">
                <ArrowUpRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Modern AI Technology Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto text-center">
          <h2 className="text-6xl md:text-7xl font-bold mb-8">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Modern</span>{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-purple-500">Ai</span>
            <br />
            <span className="text-gray-700">Technology</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Unlocking the Power of Artificial Inelegance for Intelligent Solutions
          </p>
        </div>
      </section>

      {/* AI Systems Content Section */}
      <section className="py-20 px-6 bg-gray-100">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="space-y-8">
              <h2 className="text-5xl md:text-6xl font-bold leading-tight text-gray-900">
                Artificial intelligence serve as a core building block of AI systems
              </h2>
              
              <p className="text-lg text-gray-700 leading-relaxed">
                Integrating neural network models into existing systems or software applications, enabling businesses to leverage AI capabilities seamlessly.
              </p>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  In today's fast-paced and data-driven world, businesses are constantly seeking innovative ways to gain a competitive edge, make smarter decisions, and deliver exceptional customer experiences. One technology that is transforming industries across the globe is neural networks. Harnessing the power of artificial intelligence, neural networks have the ability to analyze vast amounts of data, identify complex patterns, and make accurate predictions, enabling businesses to unlock new opportunities and drive growth.
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Tech Categories List */}
              <div className="bg-white rounded-3xl p-8 space-y-4">
                {techCategories.map((category, index) => (
                  <div key={index} className="text-gray-400 text-xl py-2 border-b border-gray-200 last:border-0">
                    {category}
                  </div>
                ))}
              </div>

              {/* Download Brochure Card */}
              <div className="relative bg-gradient-to-br from-slate-700 to-slate-900 rounded-3xl p-10 h-64 overflow-hidden">
                {/* Curved line pattern */}
                <div className="absolute inset-0 opacity-20">
                  {[...Array(8)].map((_, i) => (
                    <div 
                      key={i}
                      className="absolute right-0 w-full border-t border-cyan-400"
                      style={{ 
                        top: `${15 + i * 12}%`,
                        borderRadius: '50%',
                        transform: `translateX(${i * 3}px)`
                      }}
                    ></div>
                  ))}
                </div>

                {/* <div className="relative h-full flex flex-col justify-center">
                  <h3 className="text-white text-3xl font-bold leading-tight">
                    Download<br />
                    Comprehensive<br />
                    Services Brochure
                  </h3>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-6 bg-white">
        <div className="container mx-auto max-w-5xl">
          <h2 className="text-5xl md:text-6xl font-bold mb-16 text-gray-900">
            The essential information about
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border-b border-gray-200">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                  className="w-full flex items-center justify-between py-6 text-left group"
                >
                  <span className="text-2xl font-bold text-gray-900 group-hover:text-blue-500 transition">
                    {faq.question}
                  </span>
                  <div className="flex-shrink-0 ml-4">
                    {openFaq === index ? (
                      <ChevronUp className="w-6 h-6 text-gray-900" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-900" />
                    )}
                  </div>
                </button>
                
                {openFaq === index && (
                  <div className="pb-6 pr-12">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Scroll to Top Button */}
      <button 
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className="fixed bottom-8 right-8 w-14 h-14 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full flex items-center justify-center text-white shadow-xl hover:shadow-2xl transition-all transform hover:scale-110 z-40"
      >
        <ChevronUp className="w-6 h-6" />
      </button>
    </div>
  );
};

export default Services;